import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import {
    HydraTable,
    Heading,
    TextFilter,
    DateRangeFilter,
    SelectFilter,
    StyledCheckbox,
    TimeAgo,
} from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'
import { useSettings } from '@peracto/peracto-hooks'
import { formatAsCurrency } from '../OrdersView'
import { toast } from 'react-toastify'
import download from 'downloadjs'
import axios from 'axios'
import dayjs from 'dayjs'
import startCase from 'lodash/startCase'
import { PickListActionPanel } from './PickList'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const OrdersList = () => {
    // Default visible columns - key = column accessor
    const [selectedColumns, onSelectColumn] = useState({
        'billingAddress.surname': false,
        'deliveryAddress.postalCode': false,
    })
    const [showExportFilter, setShowExportFilter] = useState(false)
    const [exportStartDate, setExportStartDate] = useState(null)
    const [exportEndDate, setExportEndDate] = useState(null)
    const [isExporting, setIsExporting] = useState(false)
    const {
        values: { orderStatuses },
    } = useSettings()
    const { client } = useClient()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const { orders } = config.get('features', {})
    const API_URL = config.get('api')
    const orderExports = orders?.exportOrderData || false
    const pickLists = orders?.pickLists || false

    const exportOrderData = async () => {
        if (!exportStartDate || !exportEndDate) return

        const startFormatted = dayjs(exportStartDate).format('YYYY-MM-DD')
        const endFormatted = dayjs(exportEndDate).format('YYYY-MM-DD')
        const start = `${startFormatted} 00:00:00`
        const end = `${endFormatted} 23:59:59`

        setIsExporting(true)
        try {
            const { data } = await axios.get(
                `${API_URL}/export/order.csv?createdAt[operator]=between&createdAt[value][start]=${start}&createdAt[value][end]=${end}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            )

            download(data, `order_export_${startFormatted}_to_${endFormatted}.csv`)
            setShowExportFilter(false)
            setExportStartDate(null)
            setExportEndDate(null)
        } catch (e) {
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        } finally {
            setIsExporting(false)
        }
    }

    const tableColumns = [
        {
            Header: 'Order Reference',
            accessor: 'reference',
            sortType: 'basic',
            Filter: props => <TextFilter label="Order Reference" {...props} />,
            Cell: row => <Link to={row.row.original.id}>{row.cell.value || '-'}</Link>,
            selectable: false,
        },
        {
            Header: 'Email',
            accessor: 'email',
            sortType: 'basic',
            Filter: props => <TextFilter label="Email" {...props} />,
            selectable: false,
        },
        {
            Header: 'Billing Surname',
            accessor: 'billingAddress.surname',
            sortType: 'basic',
            Filter: props => <TextFilter label="Billing Surname" {...props} />,
            show: selectedColumns['billingAddress.surname'],
            selectable: true,
        },
        {
            Header: 'Delivery Surname',
            accessor: 'deliveryAddress.surname',
            sortType: 'basic',
            Filter: props => <TextFilter label="Delivery Surname" {...props} />,
            selectable: false,
        },
        {
            Header: 'Delivery Postcode',
            accessor: 'deliveryAddress.postalCode',
            sortType: 'basic',
            Filter: props => <TextFilter label="Delivery Postcode" {...props} />,
            show: selectedColumns['deliveryAddress.postalCode'],
            selectable: true,
        },
        {
            Header: 'Total (inc. tax)',
            accessor: 'totalIncTaxAfterDiscount',
            sortType: 'basic',
            Filter: props => (
                <TextFilter type="number" step={0.01} label="Total (inc. tax)" {...props} />
            ),
            Cell: row => formatAsCurrency(row.cell.value, row?.cell?.row?.original?.currency),
            selectable: false,
        },
        {
            Header: 'Total (exc. tax)',
            accessor: 'totalExcTaxAfterDiscount',
            sortType: 'basic',
            Filter: props => (
                <TextFilter type="number" step={0.01} label="Total (exc. tax)" {...props} />
            ),
            Cell: row => formatAsCurrency(row.cell.value, row?.cell?.row?.original?.currency),
            selectable: false,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sortType: 'basic',
            Filter: props => (
                <SelectFilter
                    label="Status"
                    options={[
                        { value: '', label: 'Please select...' },
                        ...(orderStatuses && orderStatuses?.length > 0
                            ? orderStatuses?.map(status => ({
                                  value: status,
                                  label: startCase(status.replace(/_/g, ' ')),
                              }))
                            : []),
                    ]}
                    {...props}
                />
            ),
            Cell: row => (
                <span className="!capitalize">{startCase(row.cell.value.replace(/_/g, ' '))}</span>
            ),
            selectable: false,
        },
        {
            Header: 'Order Created',
            accessor: 'createdAt',
            Filter: props => <DateRangeFilter label="Order Created" {...props} />,
            Cell: ({ cell: { value } }) =>
                new Date(value) > 0 ? (
                    <TimeAgo title={formatDate(new Date(value))} date={value} />
                ) : (
                    '-'
                ),
            selectable: false,
        },
    ]

    if (pickLists) {
        tableColumns.unshift({
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <StyledCheckbox {...row.getToggleRowSelectedProps()} />,
            selectable: false,
        })
    }

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => tableColumns)
    const onFetchData = options => client(GET_LIST, 'orders', options)

    return (
        <>
            <Helmet>
                <title>Orders | Peracto</title>
            </Helmet>
            <Heading name="Orders">
                <div className="!ml-2 !flex !items-center">
                    {orderExports === true && (
                        <button
                            className="!mr-2 btn btn-outline-primary"
                            onClick={() => setShowExportFilter(show => !show)}
                            data-testid="export-order-data"
                        >
                            <FontAwesomeIcon icon={faDownload} className="!mr-2" />
                            Export Order Data
                        </button>
                    )}

                    {orders?.createOrders && (
                        <Link
                            className="btn btn-primary"
                            to="/orders/create"
                            data-testid="create-order"
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                            Create Order
                        </Link>
                    )}
                </div>
            </Heading>

            {orderExports === true && showExportFilter && (
                <div className="row !mb-4">
                    <div className="col">
                        <div className="card">
                            <div className="card-body">
                                <div className="form-row !items-end">
                                    <div className="col-12 col-md-auto !mb-4 md:!mb-0">
                                        <label className="!block !mb-1">
                                            <small className="!font-bold">Start Date</small>
                                        </label>
                                        <DatePicker
                                            className="form-control"
                                            wrapperClassName="!block"
                                            placeholderText="DD/MM/YYYY"
                                            dateFormat="dd/MM/yyyy"
                                            selected={exportStartDate}
                                            openToDate={new Date()}
                                            onChange={date => {
                                                setExportStartDate(date)
                                                if (
                                                    exportEndDate &&
                                                    dayjs(date) > dayjs(exportEndDate)
                                                ) {
                                                    setExportEndDate(null)
                                                }
                                            }}
                                            selectsStart
                                            startDate={exportStartDate}
                                            endDate={exportEndDate}
                                        />
                                    </div>
                                    <div className="col-12 col-md-auto !mb-4 md:!mb-0">
                                        <label className="!block !mb-1">
                                            <small className="!font-bold">End Date</small>
                                        </label>
                                        <DatePicker
                                            className="form-control"
                                            wrapperClassName="!block"
                                            placeholderText="DD/MM/YYYY"
                                            dateFormat="dd/MM/yyyy"
                                            selected={exportEndDate}
                                            openToDate={new Date()}
                                            onChange={date => setExportEndDate(date)}
                                            selectsEnd
                                            startDate={exportStartDate}
                                            endDate={exportEndDate}
                                            minDate={exportStartDate}
                                        />
                                    </div>
                                    <div className="col-12 col-md-auto">
                                        <button
                                            className="btn btn-primary !mr-2"
                                            onClick={exportOrderData}
                                            disabled={
                                                !exportStartDate || !exportEndDate || isExporting
                                            }
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="!mr-2" />
                                            {isExporting ? 'Exporting...' : 'Download'}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setShowExportFilter(false)
                                                setExportStartDate(null)
                                                setExportEndDate(null)
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                actionPanelContent={PickListActionPanel}
                                defaultFilters={{
                                    reference: '',
                                    email: '',
                                    status: '',
                                    createdAt: '',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default OrdersList
